import React, { useState } from "react";
import { MdArrowForward } from "react-icons/md";
import { useLoanApply } from "./Component/LoanApplyContext";

const raleway = { fontFamily: "'Geist', sans-serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };
const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };

const ANNUAL_RATE = 0.24;

const AMOUNT_MIN = 50000;
const AMOUNT_MAX = 20000000;
const AMOUNT_STEP = 50000;

// Tenor handled in months. The minimum (3 months) is shown as "90 days".
const TENOR_MIN = 3;
const TENOR_MAX = 36;
const TENOR_STEP = 1;

const labelStyle = {
  ...raleway,
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#FFFFFF",
};

const displayBoxStyle = {
  ...raleway,
  width: "100%",
  boxSizing: "border-box",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "14px",
  padding: "18px 24px",
  fontSize: "22px",
  fontWeight: 500,
  color: "#FFFFFF",
};

const rangeLabelStyle = {
  ...raleway,
  fontWeight: 500,
  fontSize: "14px",
  color: "rgba(255,255,255,0.5)",
};

const resultLabelStyle = {
  ...raleway,
  fontWeight: 600,
  fontSize: "13px",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.55)",
};

const resultValueStyle = {
  ...instrumentSerif,
  fontWeight: 400,
  fontSize: "clamp(28px, 3.33vw, 48px)",
  lineHeight: 1.1,
  color: "#FFFFFF",
};

const LoanCalculator = () => {
  const [amount, setAmount] = useState(5000000);
  const [tenorMonths, setTenorMonths] = useState(12);
  const { openApply } = useLoanApply();

  // Annual rate prorated over the selected tenor.
  const interest = amount * ANNUAL_RATE * (tenorMonths / 12);
  const totalPayout = amount + interest;

  const fmt = (n) => `₦${Math.round(n).toLocaleString("en-NG")}`;
  const tenorLabel =
    tenorMonths <= TENOR_MIN ? "90 days" : `${tenorMonths} months`;

  return (
    <>
      <style>{`
				.calc-slider {
					-webkit-appearance: none;
					appearance: none;
					width: 100%;
					height: 20px;
					background: transparent;
					cursor: pointer;
					outline: none;
					margin: 0;
				}
				.calc-slider::-webkit-slider-runnable-track {
					height: 4px;
					border-radius: 999px;
					background: rgba(255,255,255,0.15);
				}
				.calc-slider::-webkit-slider-thumb {
					-webkit-appearance: none;
					appearance: none;
					width: 20px;
					height: 20px;
					border-radius: 50%;
					background: #86C7A8;
					border: none;
					margin-top: -8px;
				}
				.calc-slider::-moz-range-track {
					height: 4px;
					border-radius: 999px;
					background: rgba(255,255,255,0.15);
				}
				.calc-slider::-moz-range-thumb {
					width: 20px;
					height: 20px;
					border-radius: 50%;
					background: #86C7A8;
					border: none;
				}
				@media (max-width: 900px) {
					.loan-calc-row { flex-direction: column-reverse !important; }
					.loan-calc-card { width: 100% !important; }
					.loan-calc-text { padding-top: 0 !important; }
				}
			`}</style>

      <section
        style={{
          background: "#FFFFFF",
          width: "100%",
          boxSizing: "border-box",
          padding: "clamp(48px, 6.94vw, 100px) clamp(20px, 5.56vw, 80px)",
        }}
      >
        <div
          className="loan-calc-row"
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "39px",
            maxWidth: "1280px",
            width: "100%",
            alignItems: "flex-start",
          }}
        >
          {/* Calculator card */}
          <div
            className="loan-calc-card"
            style={{
              flexShrink: 0,
              width: "592px",
              background: "#132A22",
              borderRadius: "24px",
              padding: "clamp(28px, 3vw, 44px)",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              gap: "36px",
            }}
          >
            {/* Amount */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <label style={labelStyle}>Amount</label>
              <div style={displayBoxStyle}>{fmt(amount)}</div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={rangeLabelStyle}>{fmt(AMOUNT_MIN)}</span>
                  <span style={rangeLabelStyle}>{fmt(AMOUNT_MAX)}</span>
                </div>
                <input
                  className="calc-slider"
                  type="range"
                  min={AMOUNT_MIN}
                  max={AMOUNT_MAX}
                  step={AMOUNT_STEP}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  aria-label="Loan amount"
                />
              </div>
            </div>

            {/* Tenor */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <label style={labelStyle}>Tenor</label>
              <div style={displayBoxStyle}>{tenorLabel}</div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={rangeLabelStyle}>Min: 90days</span>
                  <span style={rangeLabelStyle}>Max: 36 months</span>
                </div>
                <input
                  className="calc-slider"
                  type="range"
                  min={TENOR_MIN}
                  max={TENOR_MAX}
                  step={TENOR_STEP}
                  value={tenorMonths}
                  onChange={(e) => setTenorMonths(Number(e.target.value))}
                  aria-label="Loan tenor in months"
                />
              </div>
            </div>

            {/* Divider */}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }} />

            {/* Results */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                gap: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <span style={resultLabelStyle}>Expected Interest</span>
                <span style={resultValueStyle}>{fmt(interest)}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  textAlign: "right",
                }}
              >
                <span style={resultLabelStyle}>Total Payout at Maturity</span>
                <span style={resultValueStyle}>{fmt(totalPayout)}</span>
              </div>
            </div>
          </div>

          {/* Right text */}
          <div
            className="loan-calc-text"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              maxWidth: "649px",
              paddingTop: "10px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "clamp(12px, 1.39vw, 20px)",
              }}
            >
              {/* Label */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#295B4A",
                    flexShrink: 0,
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    ...geistMono,
                    fontWeight: 500,
                    fontSize: "12px",
                    lineHeight: "1",
                    letterSpacing: "0.1em",
                    color: "#295B4A",
                    textTransform: "uppercase",
                  }}
                >
                  Loans Calculator
                </span>
              </div>

              <h2
                style={{
                  ...instrumentSerif,
                  fontWeight: 400,
                  fontSize: "clamp(28px, 4.17vw, 60px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.01em",
                  color: "#132A22",
                  margin: 0,
                }}
              >
                Estimate your monthly repayment.
              </h2>
            </div>
            <p
              style={{
                ...raleway,
                fontWeight: 400,
                fontSize: "clamp(14px, 1.39vw, 20px)",
                lineHeight: "32px",
                color: "#5E5E5E",
                margin: 0,
              }}
            >
              Choose an amount and repayment period to get a quick estimate
              before applying.
            </p>
            <p
              style={{
                ...raleway,
                fontWeight: 500,
                fontSize: "12px",
                lineHeight: "18px",
                color: "#295B4A",
                margin: 0,
              }}
            >
              Estimates are illustrative and subject to final approval terms.
            </p>

            <button
              type="button"
              onClick={() =>
                openApply({
                  amount,
                  note: `Calculator selection — Amount: ${fmt(
                    amount
                  )} · Tenor: ${tenorLabel} · Estimated total payout: ${fmt(
                    totalPayout
                  )}`,
                })
              }
              style={{
                ...raleway,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                alignSelf: "flex-start",
                marginTop: "16px",
                height: "52px",
                padding: "0 24px",
                backgroundColor: "transparent",
                border: "1px solid #DEDEDE",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "20px",
                color: "#132A22",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Apply with these terms
              <MdArrowForward size={20} />
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default LoanCalculator;
